import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Briefcase, CheckCircle2, Paperclip } from "lucide-react";
import { toast } from "sonner";

export default function ApplyForm() {
  const { vid } = useParams();
  const [vac, setVac] = useState(null);
  const [err, setErr] = useState(false);
  const [done, setDone] = useState(false);
  const [portalToken, setPortalToken] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", cover_note: "", right_to_work: false, consent: false });
  const [ans, setAns] = useState({});
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setAnswer = (id, v) => setAns((s) => ({ ...s, [id]: v }));

  useEffect(() => {
    api.get(`/vacancies/${vid}/public`).then((r) => setVac(r.data)).catch(() => setErr(true));
  }, [vid]);

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email) { toast.error("Please complete your name and email"); return; }
    if (!f.consent) { toast.error("Please tick consent to submit"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("answers", JSON.stringify(ans));
      if (cvFile) fd.append("cv", cvFile);
      const { data } = await api.post(`/vacancies/${vid}/apply`, fd);
      setPortalToken(data.portal_token);
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-[#EEF2F7] p-6"><div className="bg-white border border-slate-200 rounded-xl p-10 text-center max-w-md"><p className="text-lg font-semibold text-[#0B1A2F]">Vacancy unavailable</p><p className="text-sm text-slate-500 mt-2">This role isn't currently open for applications.</p></div></div>;
  if (!vac) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#EEF2F7]">
      <header className="h-16 bg-gradient-to-r from-[#091E36] via-[#0B1A2F] to-[#0F2C50] text-white flex items-center px-6">
        <span className="text-2xl font-extrabold text-[#2EA3FD]">CASS</span><span className="ml-2 text-lg font-semibold">Careers</span>
      </header>
      <div className="max-w-2xl mx-auto p-6">
        {done ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="apply-success">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={40} />
            <p className="text-xl font-bold text-[#0B1A2F]">Application received</p>
            <p className="text-sm text-slate-500 mt-2">Thank you for applying for <strong>{vac.title}</strong>. Our team will be in touch.</p>
            {portalToken && (
              <a href={`/portal/${portalToken}`} className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-[#2EA3FD] hover:underline" data-testid="apply-portal-link">Track your application →</a>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-5">
              <div className="flex items-center gap-2 text-[#2EA3FD]"><Briefcase size={18} /><span className="text-xs font-semibold uppercase tracking-wider">Apply for this role</span></div>
              <h1 className="text-2xl font-extrabold text-[#0B1A2F] mt-1">{vac.title}</h1>
              <p className="text-sm text-slate-500">{[vac.department, vac.location].filter(Boolean).join(" · ")}</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-slate-600">
                {(vac.salary_min || vac.salary_max) && <span>£{vac.salary_min || "?"}{vac.salary_max ? ` – £${vac.salary_max}` : ""}</span>}
                {vac.hours_per_week && <span>{vac.hours_per_week} hrs/week</span>}
                {vac.closing_date && <span>Closes {fmtDate(vac.closing_date)}</span>}
              </div>
              {vac.job_description && <p className="text-sm text-slate-600 mt-4 whitespace-pre-wrap">{vac.job_description}</p>}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">First name</Label><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="apply-first-name" /></div>
                <div><Label className="text-xs text-slate-500">Last name</Label><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="apply-last-name" /></div>
                <div><Label className="text-xs text-slate-500">Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" /></div>
                <div><Label className="text-xs text-slate-500">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="apply-phone" /></div>
              </div>
              <div><Label className="text-xs text-slate-500">Cover note</Label><Textarea rows={4} value={f.cover_note} onChange={(e) => set("cover_note", e.target.value)} placeholder="Tell us why you're a great fit…" data-testid="apply-cover" /></div>
              {(vac.application_questions || []).map((q) => (
                <div key={q.id} data-testid={`apply-question-${q.id}`}>
                  <Label className="text-xs text-slate-500">{q.label}{q.required ? " *" : ""}</Label>
                  {q.type === "textarea" ? (
                    <Textarea rows={3} value={ans[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`apply-answer-${q.id}`} />
                  ) : q.type === "select" ? (
                    <select value={ans[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} className="w-full border border-slate-200 rounded-md h-10 px-3 text-sm" data-testid={`apply-answer-${q.id}`}>
                      <option value="">Choose…</option>{(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : q.type === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm text-slate-600 mt-1"><Checkbox checked={!!ans[q.id]} onCheckedChange={(v) => setAnswer(q.id, !!v)} data-testid={`apply-answer-${q.id}`} />Yes</label>
                  ) : (
                    <Input value={ans[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`apply-answer-${q.id}`} />
                  )}
                </div>
              ))}
              <div>
                <Label className="text-xs text-slate-500">CV / Résumé (optional)</Label>
                <label className="mt-1 flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-4 py-3 cursor-pointer hover:border-[#2EA3FD] hover:bg-[#F5FAFF] transition-colors" data-testid="apply-cv-label">
                  <Paperclip size={16} className="text-[#2EA3FD]" />
                  <span className="text-sm text-slate-600 truncate">{cvFile ? cvFile.name : "Attach your CV (PDF or Word, max 10MB)"}</span>
                  <input type="file" accept=".pdf,.doc,.docx,.rtf,.odt,.txt" className="hidden"
                    onChange={(e) => { const file = e.target.files?.[0]; if (file && file.size > 10 * 1024 * 1024) { toast.error("File is too large (max 10MB)"); return; } setCvFile(file || null); }}
                    data-testid="apply-cv-input" />
                </label>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-600"><Checkbox checked={f.right_to_work} onCheckedChange={(v) => set("right_to_work", !!v)} data-testid="apply-rtw" />I confirm I have the right to work in the UK.</label>
              <label className="flex items-start gap-2 text-sm text-slate-600"><Checkbox checked={f.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="apply-consent" />I consent to CASS storing my details for this recruitment process.</label>
              <Button className="w-full bg-[#2EA3FD] hover:bg-[#1B8EE6] font-semibold" onClick={submit} disabled={submitting} data-testid="apply-submit-btn">{submitting ? "Submitting…" : "Submit application"}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
